import { FunctionComponent, useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { classNames } from "@/utils/classNames";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { useSearch } from "@/utils";
import {
	clearInbox,
	listSelector,
	setCurrentLeadIndex,
} from "@/pages/Private/redux/list/list.slice";
import { InclusionFilterStatus, ListSourceType, PriorityFilterStatus } from "@/enum/list.enum";
import {
	useGetReplyCountsQuery,
	useLazyGetAllProspectsQuery,
} from "@/pages/Private/redux/list/list.api";
import { copywritingOrchestrationSelector } from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.slice";

import { useGetSendersUsersQuery } from "@/pages/Private/redux/admin/admin.api";
import { useGetCompanyLifecycleQuery } from "@/pages/Private/redux/company/company.api";

import { authSelector } from "@/pages/Public/redux/auth.slice";

import { NoSenderModal } from "@/components/NoSenderModal/NoSenderModal";

import { DraftReplies } from "@/enum/draft-replies.enum";
import { useTour } from "@/components/Tour/useTour";
import { useSnackBar } from "@/utils/useSnackbar";

import { MessagesFilterBar } from "../../components/MessagesFilterBar/MessagesFilterBar";
import { BEST_REPLY_SORT_KEY } from "../../../List/components/SortByDropdown/sort-by-dropdown.types";

import { FeaturesCodes } from "../../../Users/enum/codes";
import { FilterModal, IFilterWithValues } from "../../../List/components/FilterModal";
import { ListPerson } from "../../../List/schema/list";

import { useInboxSingleProspectActions } from "./useInboxSingleProspectActions";
import { LeadDetailRail } from "../../components/LeadDetailRail";
import { useBulkAssign } from "../../../List/utils/useBulkAssign";
import { useAddInboxChat } from "../../../List/components/CampaignOverview/utils/useAddInboxChat";
import { useCopywritingSocket } from "../../components/GenerateBar/useCopywritingSocket";
import { CustomListsAutocompleteForSequencerItem } from "../../../List/components/CustomListsAutocompleteForSequencer";
import { ListContextHeader } from "../../components/ListContextHeader";
import { ListScopeFilter } from "../../components/ListScopeFilter/ListScopeFilter";
import {
	LIST_SCOPE_RELEVANCE_STATUSES,
	ListScope,
} from "../../components/ListScopeFilter/listScope";
import { useListScopeLists } from "../../components/ListScopeFilter/useListScopeLists";
import { ReadOnlyPlanBanner } from "../../components/ReadOnlyPlanBanner";
import { resolveMessagesAccessMode } from "../../utils/messagesAccessMode";
import { ReplyTab, resolveEffectiveSenders } from "./inboxReplyTabs";
import { InboxReplyTabStrip } from "./InboxReplyTabStrip";
import { InboxConfirmModals } from "./InboxConfirmModals";
import { InboxLeadsColumn } from "./InboxLeadsColumn";
import { InboxFilterDropdowns } from "./InboxFilterDropdowns";
import { InboxThreadPane } from "./InboxThreadPane";
import { useInboxInitialSenders } from "./useInboxInitialSenders";
import { useInboxQuickFilters } from "./useInboxQuickFilters";
import { useInboxUrlSync } from "./useInboxUrlSync";

export const Inbox: FunctionComponent = () => {
	const { t } = useTranslation();

	const { user } = useAppSelector(authSelector);

	const { renderChatlog, renderSendButtons } = useAddInboxChat({
		prefillDrafts: true,
		undoableSend: true,
		hideGenerateBar: true,
	});
	const { sortingInbox, inbox, currentLeadIndex, meta } = useAppSelector(listSelector);
	const { isGenerating, alreadyInProgress } = useAppSelector(copywritingOrchestrationSelector);

	// The copywriting socket must be owned at page level — GenerateBar only
	// reads the orchestration slice (see the note in GenerateBar.tsx). Without
	// this, no client ever joins the gateway room and stage/complete events for
	// generations started from the inbox are emitted into the void.
	useCopywritingSocket({
		companyId: user?.company?.id,
		prospectId: inbox[currentLeadIndex]?.prospect?.id,
		enabled: isGenerating || alreadyInProgress,
	});
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();
	const [showArchiveModal, setShowArchiveModal] = useState<boolean>(false);
	const [showDoneModal, setShowDoneModal] = useState<boolean>(false);

	// Draft replies tour
	const { startTour } = useTour();
	const draftReplies = user?.company?.draftReplies;
	const hasDraftsEnabled = draftReplies === DraftReplies.ONE || draftReplies === DraftReplies.THREE;

	useEffect(() => {
		if (!hasDraftsEnabled || inbox.length === 0) {
			return;
		}

		const timer = setTimeout(() => {
			startTour("draft-replies", { draftReplies });
		}, 1000);

		return () => clearTimeout(timer);
	}, [hasDraftsEnabled, inbox.length, draftReplies]);

	const {
		handleBulkStatusChange,
		bulkChangeStatusIsLoading,
		createBlacklistIsLoading,
		renderBlacklistModal,
		setShowBlacklistModal,
	} = useBulkAssign();

	const { data: sendingUsers } = useGetSendersUsersQuery(undefined, {
		refetchOnMountOrArgChange: true,
	});
	const { data: lifecycle } = useGetCompanyLifecycleQuery();
	const accessMode = resolveMessagesAccessMode({
		lifecycleState: lifecycle?.state,
		hasSendingUsers: (sendingUsers?.data?.length ?? 0) > 0,
		sendingUsersLoaded: Boolean(sendingUsers?.data),
	});

	const {
		lists: listData,
		filterProps: listFilterProps,
		resetPage: resetListPage,
	} = useListScopeLists(ListScope.INBOX);

	const getSelectedListsFromUrl = (): CustomListsAutocompleteForSequencerItem[] => {
		const params = new URLSearchParams(location.search);
		const listIdsParam = params.get("listIds");

		return listIdsParam
			? (JSON.parse(listIdsParam) as CustomListsAutocompleteForSequencerItem[])
			: [];
	};

	const [selectedLists, setSelectedLists] =
		useState<CustomListsAutocompleteForSequencerItem[]>(getSelectedListsFromUrl());

	const { doesSendingUsersHaveMe, selectedSenders, setSelectedSenders } = useInboxInitialSenders({
		sendingUsers,
		userId: user?.id,
		userFirstName: user?.firstName,
		userLastName: user?.lastName,
	});

	// Open by default (PREC-3850): the docked rail reserves its own width.
	const [showSingleLead, setShowSingleLead] = useState<boolean>(true);
	const [showLeads, setShowLeads] = useState<boolean>(true);

	const [replyTab, setReplyTab] = useState<ReplyTab>(ReplyTab.MY);

	// L3-nav quick filters (PREC-3599): qualified-leads (lead-priority) + reply-sentiment, URL-synced.
	const { priorityFilterStatus, setPriorityFilterStatus, sentimentFilter, setSentimentFilter } =
		useInboxQuickFilters();

	// Company sending users excluding the current user — the "Team replies" scope.
	const teamMembers = useMemo(
		() => (sendingUsers?.data ?? []).filter((member) => member.id !== user?.id),
		[sendingUsers, user?.id]
	);

	// The sender ids actually sent to the query, derived from the active tab and
	// the (tab-scoped) senders dropdown narrowing. An empty array means no filter.
	//
	// Resolved to a serialized key first so the array memo below only produces a new
	// reference when the *contents* change. Right after mount the senders state
	// settles in two steps (sendingUsers loads, then selectedSenders is re-derived),
	// each yielding the same ids; without this the new array reference would re-run
	// the fetch effect, abort the in-flight request, and re-trigger it with identical
	// args — which leaves the lazy query pointing at the just-aborted cache entry, so
	// the My replies list never loads on first visit (PREC-3549).
	const effectiveSendersKey = useMemo(
		() =>
			resolveEffectiveSenders(
				replyTab,
				selectedSenders,
				teamMembers.map((member) => member.id as number),
				user?.id
			).join(","),
		[replyTab, selectedSenders, teamMembers, user?.id]
	);

	const effectiveSenders = useMemo<number[]>(
		() => (effectiveSendersKey ? effectiveSendersKey.split(",").map(Number) : []),
		[effectiveSendersKey]
	);

	const handleReplyTabChange = useCallback((tab: ReplyTab) => {
		setReplyTab(tab);
		setPage(1);
		setSelectedSenders([{ id: "all", title: "All" }]);
	}, []);

	const [selected, setSelected] = useState<number[]>([]);

	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const threadWrapperRef = useRef<HTMLDivElement>(null);

	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const [
		getUnassigned,
		{ isFetching: isFetchingGetUnassigned, isLoading: isLoadingGetUnassigned },
	] = useLazyGetAllProspectsQuery();

	const { limit, query, setSearchValue, searchValue } = useSearch();
	const [page, setPage] = useState<number>(1);
	const [showFilterModal, setShowFilterModal] = useState<boolean>(false);

	const loadMoreData = useCallback(() => {
		if (!isFetchingGetUnassigned && page < (meta?.pageCount || 0) && page === meta.page) {
			setPage(page + 1);
		}
	}, [page, isFetchingGetUnassigned, meta]);

	const getFilterFromUrl = () => {
		const params = new URLSearchParams(location.search);

		return JSON.parse(params.get("filter") || "{}");
	};

	const [filter, setFilter] = useState<IFilterWithValues | undefined>(getFilterFromUrl());

	const isSequencerActive = useIsFeatureActive(FeaturesCodes.SEQUENCER);

	// Mirrors senders/search/filter/list-scope selections into the URL and seeds the
	// search box from it on mount (see useInboxUrlSync.ts).
	useInboxUrlSync({
		selectedSenders,
		searchValue,
		filter,
		selectedLists,
		setSearchValue,
		navigate,
	});

	function handleSetCurrentLead(index: number) {
		dispatch(setCurrentLeadIndex(index));
	}

	useEffect(() => {
		// Wait for sendingUsers to load before firing the first request
		// to avoid the request being canceled by a subsequent re-trigger
		// when selectedSenders gets updated after sendingUsers loads
		if (!sendingUsers) {
			return;
		}

		// On the My replies tab the query must filter by the current user's id. Until
		// it is known, resolveEffectiveSenders yields [] (= no filter = all replies),
		// so firing now would issue an "all replies" request and then abort it once the
		// id arrives. Wait for the id so the first request is correct (PREC-3549).
		if (replyTab === ReplyTab.MY && !user?.id) {
			return;
		}

		// Only clear inbox on fresh loads (page 1), not when paginating
		if (page === 1) {
			dispatch(clearInbox());
			dispatch(setCurrentLeadIndex(0));
		}

		// The Inbox population, from the one module that owns the per-surface status sets.
		const assignStatus = LIST_SCOPE_RELEVANCE_STATUSES[ListScope.INBOX];

		// No manual abort here: a stale response (e.g. the previous tab's request landing after a
		// client-side tab switch) is ignored by the slice's request-id guard (see list-inbox.helper).
		getUnassigned({
			page,
			limit,
			searchValue: query,
			sorting: sortingInbox,
			assignStatus: assignStatus ?? undefined,
			filter,
			inclusionFilterStatus: [InclusionFilterStatus.ADDED],
			priorityFilterStatus,
			sentimentFilterStatus: sentimentFilter,
			senders: effectiveSenders,
			listIds:
				selectedLists?.length > 0
					? [...(selectedLists?.map((list) => list.id as number) || [])]
					: undefined,
		});
	}, [
		sendingUsers,
		filter,
		limit,
		page,
		query,
		sortingInbox,
		effectiveSenders,
		replyTab,
		selectedLists,
		priorityFilterStatus,
		sentimentFilter,
		user?.id,
	]);

	useEffect(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		timeoutRef.current = setTimeout(() => {
			if (!isSequencerActive) {
				navigate(`/app/lists/`);
			}
		}, 500);
	}, [isSequencerActive, navigate]);

	const currentListPerson: ListPerson | undefined = useMemo(() => {
		if (currentLeadIndex === undefined || currentLeadIndex < 0) {
			return undefined;
		}

		return inbox[currentLeadIndex];
	}, [inbox, currentLeadIndex]);

	const { archiveProspect, markProspectDone } = useInboxSingleProspectActions({
		listPerson: currentListPerson,
		changeStatus: handleBulkStatusChange,
		closeArchiveModal: () => setShowArchiveModal(false),
		closeDoneModal: () => setShowDoneModal(false),
		notify: handleOpenSnackbar,
		t,
	});

	const { data: replyCounts } = useGetReplyCountsQuery(
		{
			page: 1,
			limit: 10,
			searchValue: query,
			sorting: sortingInbox,
			filter,
			inclusionFilterStatus: [InclusionFilterStatus.ADDED],
			priorityFilterStatus,
			sentimentFilterStatus: sentimentFilter,
			listIds:
				selectedLists?.length > 0 ? selectedLists.map((list) => list.id as number) : undefined,
		},
		{ skip: !sendingUsers }
	);

	const listFilterElement = (
		<div className="min-w-[125px] w-[fit-content]">
			<ListScopeFilter
				{...listFilterProps}
				onApply={(value: CustomListsAutocompleteForSequencerItem[]) => {
					setSelectedLists(value);
					resetListPage();
				}}
				selectedLists={selectedLists || []}
			/>
		</div>
	);

	return (
		<>
			{accessMode === "no-senders" && <NoSenderModal />}
			<div
				className={classNames(
					"sm:block flex flex-col items-start justify-between w-full h-full overflow-hidden",
					accessMode === "no-senders" ? "blur-sm" : ""
				)}
			>
				<div className="bg-brand-white  flex flex-col items-stretch w-full h-full">
					{accessMode === "read-only" && (
						<ReadOnlyPlanBanner readOnlyUntil={lifecycle?.readOnlyUntil ?? null} />
					)}
					<ListContextHeader
						lists={listData}
						listFilter={listFilterElement}
						selectedLists={selectedLists}
						title={t("messages.pageTitle.inbox")}
					/>
					{/* Full-height docked rail (PREC-3850): the rail sits beside the tab strip,
					    filter bar and columns, spanning from the context header to the bottom. */}
					<div className="flex flex-row flex-1 min-h-0 w-full">
						<div className="flex flex-col flex-1 min-w-0">
							<InboxReplyTabStrip
								activeTab={replyTab}
								counts={replyCounts}
								onChange={handleReplyTabChange}
							/>
							<MessagesFilterBar
								className="border-b border-border-light"
								filterCount={filter ? Object.keys(filter).length : 0}
								onOpenFilter={() => setShowFilterModal(true)}
								searchValue={searchValue}
								sortExtraOptions={[
									{ orderTitle: BEST_REPLY_SORT_KEY, title: t("inbox.sortByOptions.bestReply") },
								]}
								onSearchChange={(value) => {
									setSearchValue(value);
									setPage(1);
								}}
								dropdowns={
									<InboxFilterDropdowns
										doesSendingUsersHaveMe={doesSendingUsersHaveMe}
										priorityFilterStatus={priorityFilterStatus}
										replyTab={replyTab}
										selectedSenders={selectedSenders}
										sendingUsers={sendingUsers}
										sentimentFilter={sentimentFilter}
										setPriorityFilterStatus={setPriorityFilterStatus}
										setSelectedSenders={setSelectedSenders}
										setSentimentFilter={setSentimentFilter}
										teamMembers={teamMembers}
										user={user}
										onPageReset={() => setPage(1)}
									/>
								}
							/>
							<div
								className="flex w-full flex-1 min-h-0 flex-row relative overflow-hidden"
								// overflow-hidden boxes can still be scrolled programmatically
								// (scrollIntoView, focus scrolling) and the user can never scroll
								// them back — pin the position so the filter/sender controls don't
								// get stuck clipped under the tab bar.
								onScroll={(e) => {
									e.currentTarget.scrollTop = 0;
									e.currentTarget.scrollLeft = 0;
								}}
							>
								<InboxLeadsColumn
									bulkChangeStatusIsLoading={bulkChangeStatusIsLoading}
									createBlacklistIsLoading={createBlacklistIsLoading}
									isFetching={isFetchingGetUnassigned}
									page={page}
									selected={selected}
									selectedLists={selectedLists}
									setSelected={setSelected}
									setShowBlacklistModal={setShowBlacklistModal}
									setShowDoneModal={setShowDoneModal}
									showLeads={showLeads}
									onLoadMore={loadMoreData}
									onSetCurrentLead={handleSetCurrentLead}
									onToggleShowLeads={() => setShowLeads(!showLeads)}
								/>

								<InboxThreadPane
									currentListPerson={currentListPerson}
									isLoading={isLoadingGetUnassigned}
									renderChatlog={renderChatlog}
									renderSendButtons={accessMode === "read-only" ? () => null : renderSendButtons}
									selected={selected}
									threadWrapperRef={threadWrapperRef}
								/>
							</div>
						</div>
						<LeadDetailRail
							docked
							grabberClassName="z-[50000]"
							listPerson={currentListPerson}
							setShow={setShowSingleLead}
							show={showSingleLead}
						/>
					</div>

					{showFilterModal && (
						<FilterModal
							filter={filter}
							listType={ListSourceType.CSV_ENRICHMENTS}
							setFilter={(e) => {
								setPage(1);
								setFilter(e);
							}}
							setShowFilterModal={setShowFilterModal}
							showFilterModal={showFilterModal}
						/>
					)}
					<InboxConfirmModals
						isLoading={bulkChangeStatusIsLoading}
						showArchive={showArchiveModal}
						showDone={showDoneModal}
						onCloseArchive={() => setShowArchiveModal(false)}
						onCloseDone={() => setShowDoneModal(false)}
						onConfirmArchive={archiveProspect}
						onConfirmDone={markProspectDone}
					/>
				</div>
				{currentListPerson &&
					renderBlacklistModal({
						selectedItemForConfirmation: currentListPerson,
						asUnsubscribe: true,
					})}
			</div>
			{renderMessageSnackbar()}
		</>
	);
};
